import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { getActivitiesWithTreatmentCode } from '../Activity/store/actions'
import { Spinner } from '../App/Components'
import { useAppDispatch, useAppSelector } from '../App/Hooks/reduxHooks'
import { ICustomer } from '../Customer/interfaces/ICustomer'
import Search from '../Customer/Search/Search'
import { getUsers } from '../User/Store/actions'

import { AbsoluteSpinner } from './components/AbsoluteSpinner/AbsoluteSpinner'
import { HealthInsuranceData } from './components/HealthInsuranceData/HealthInsuranceData'
import { HITable } from './components/HITable/HITable'
import { PageTitle } from './components/PageTitle/PageTitle'
import {
  createHICall,
  getCustomerInvoiceData,
  getReceiptCall,
} from './healthInsuranceAPICall'
import {
  IAPIHealthInsurance,
  IAPIInvoiceCustomer,
  IHICustomer,
  IHICustomerDetails,
  IHIInvoiceLine,
} from './interfaces/IHealthInsurance'
import { setHICustomer, setHICustomerDetails } from './store/action'
import { PageLayout } from './style'
import { transformAPIInvoiceCustomerToHICustomerDetails } from './utils/transformAPIInvoiceCustomerToHICustomerDetails'
import { transformCustomerToAPICustomer } from './utils/transformCustomerToAPICustomer'
import { transformCustomerToHICustomer } from './utils/transformCustomerToHICustomer'
import { transformLineToAPILine } from './utils/transformLineToAPILine'

export const HealthInsuranceN = () => {
  const [linesFromReceipt, setLinesFromReceipt] = useState<IHIInvoiceLine[]>()
  const [isSubmittingForm, setIsSubmittingForm] = useState<boolean>(false)

  const [prefillCustomerData, setPrefillCustomerData] =
    useState<IHICustomerDetails>()

  const { receiptId } = useParams<{ [key: string]: string }>()
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const {
    healthInsuranceCustomer,
    users,
    activityWithCode,
    healthInsuranceCustomerDetails,
  } = useAppSelector(state => ({
    healthInsuranceCustomer: state.healthInsurance.customer,
    healthInsuranceCustomerDetails: state.healthInsurance.customerDetails,
    users: state.users.list,
    activityWithCode: state.activities.activityWithCode,
  }))

  useEffect(() => {
    if (!users) {
      dispatch(getUsers())
    }
    if (!activityWithCode) {
      dispatch(getActivitiesWithTreatmentCode())
    }
    if (receiptId) {
      getReceiptCall(receiptId).then(async response => {
        const receiptData = response.data.data.receiptCustomers[0]
        const customerData: IHICustomer = {
          customerId: receiptData.customerId,
          lastName: receiptData.lastName,
          firstName: receiptData.firstName,
        }
        const customerDetails: IHICustomerDetails = {
          address: receiptData.street || '',
          postalCode: receiptData.postal || '',
          city: receiptData.city || '',
          country: receiptData.invoice_country || '',
          healthInsuranceNumber: receiptData.invoice_health_insurance_number,
          birthDay: receiptData.birthday || moment().format('DD-MM-YYYY'),
          gender: receiptData.gender || 'X',
          bsnCode: null,
          extraInfo: null,
          isReadyForTable: false,
        }

        const tableLines = receiptData.receiptLines.map(receiptLine => ({
          id: receiptLine.id,
          treatmentDate:
            response.data.data.appointment_date ||
            moment().format('DD-MM-YYYY'),
          personName: receiptLine.person.name,
          personAgbCode: receiptLine.person.AGBCode || '',
          personUuid: receiptLine.person.uuid,
          treatmentName: receiptLine.activity.name,
          treatmentCode: receiptLine.activity.treatmentCode || '',
          treatmentUuid: receiptLine.activity.uuid,
          amount: receiptLine.amount ? receiptLine.amount : 0,
          unitPrice: receiptLine.price ? receiptLine.price : 0,
          unitPriceExcl:
            receiptLine.price && receiptLine.vatPercentage
              ? Math.round(
                  ((receiptLine.price * 100) /
                    (100 + Number(receiptLine.vatPercentage)) +
                    Number.EPSILON) *
                    100,
                ) / 100
              : 0,
          discount: receiptLine.discount ? receiptLine.discount : 0,
          vatPercentage: receiptLine.vatPercentage
            ? receiptLine.vatPercentage
            : 0,
          lineTotal: receiptLine.total,
        }))
        setPrefillCustomerData(customerDetails)
        dispatch(setHICustomerDetails(customerDetails))
        dispatch(setHICustomer(customerData))
        setLinesFromReceipt(tableLines)
      })
    }
  }, [])

  const updateCustomer = async (newCustomerData: ICustomer | null) => {
    if (!newCustomerData) return
    const transformCustomer = transformCustomerToHICustomer(newCustomerData)
    const apiInvoiceCustomerResponse = await getCustomerInvoiceData(
      newCustomerData.uuid,
    )
    const apiInvoiceCustomer: IAPIInvoiceCustomer =
      apiInvoiceCustomerResponse.data.data
    const transformCustomerDetails =
      transformAPIInvoiceCustomerToHICustomerDetails(apiInvoiceCustomer)
    setPrefillCustomerData(transformCustomerDetails)
    dispatch(setHICustomerDetails(transformCustomerDetails))
    dispatch(setHICustomer(transformCustomer))
  }

  if (receiptId && !linesFromReceipt) {
    return (
      <PageLayout>
        <PageTitle customer={healthInsuranceCustomer} />
        <Spinner />
      </PageLayout>
    )
  }

  if (!healthInsuranceCustomer) {
    return (
      <PageLayout>
        <PageTitle customer={healthInsuranceCustomer} />
        <Search
          onSelected={newCustomerData => updateCustomer(newCustomerData)}
          onDeselected={() => updateCustomer(null)}
          withInvoiceInformation
        />
      </PageLayout>
    )
  }

  if (!prefillCustomerData) {
    return (
      <PageLayout>
        <PageTitle customer={healthInsuranceCustomer} />
        <Spinner />
      </PageLayout>
    )
  }

  if (
    !healthInsuranceCustomerDetails ||
    healthInsuranceCustomerDetails.isReadyForTable === false
  ) {
    return (
      <PageLayout>
        <PageTitle customer={healthInsuranceCustomer} />
        <HealthInsuranceData prefillCustomerData={prefillCustomerData} />
      </PageLayout>
    )
  }

  const onSaveTable = async (hILines: IHIInvoiceLine[]) => {
    setIsSubmittingForm(true)
    const APIObject: IAPIHealthInsurance = {
      customer: transformCustomerToAPICustomer({
        customer: healthInsuranceCustomer,
        details: healthInsuranceCustomerDetails,
      }),
      lines: hILines.map(hILine => transformLineToAPILine(hILine)),
      receipt_id: receiptId || null,
    }
    const response = await createHICall(APIObject)
    const isSuccess = response.status === 201
    if (isSuccess) {
      window.location.href = '/register/invoice/view'
    } else {
      console.log('Something went wrong', response)
    }
  }

  return (
    <>
      {isSubmittingForm ? (
        <AbsoluteSpinner
          loadingText={t('Submitting health insurance invoice.')}
        />
      ) : null}
      <PageLayout>
        <PageTitle customer={healthInsuranceCustomer} />
        <HITable defaultLines={linesFromReceipt} onSaveTable={onSaveTable} />
      </PageLayout>
    </>
  )
}
